import React, { useEffect, useState, useContext } from 'react';
import 'src/components/shared/Agile/AgileBase.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Textarea from '@amzn/meridian/textarea';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import { useAppSelector } from '@store/hooks';
import { selectProgram } from '@store/slices/programSlice';
import AtAgileHeader from 'src/components/agile/agileComponents/AtAgileHeader';
import AtFaceUnitEditTable from '@components/agile/agileComponents/AtAddNewAsin/AtFaceUnitEditTable';
import { useLocation } from 'react-router-dom';
import AtAddRecommendationTable from '@components/agile/agileComponents/AtAddRecommendationTable';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { InputAsinsValidationUtils, getAsinValidationMessage } from '@components/agile/utils/InputAsinsValidationUtils';
import { AgileRecommendationInput } from '@components/agile/types/AgileRecommendationTypes';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { AsinMetadataEntry } from '@graphql/generated/graphql';
import AtModifyRecommendationTable from '@components/agile/agileComponents/AtModifyRecommendationTable';
import AgileContext from '@components/agile/context/AgileContext';
import AtAcrossZoneMain from '@components/agile/agileComponents/AtAcrossZoneOperation/AtAcrossZoneMain';
import { bulkOperationWarning } from 'src/components/agile/utils/ToasterUtils';
import Toaster, { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import { FixtureComponent, zoneColors } from './types/zoneMapType';
import FixtureBox from './agileComponents/FixtureComponent';
import { getZoneColor, getConcatnatedSubcategoryName } from './utils/FixtureDataUtils';

export interface LocationParams {
  pathname: string;
  state: AgileURLParameterTypes;
  search: string;
  hash: string;
  key: string;
}

export interface AgileManagementProps {
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (value: AgileURLParameterTypes) => void;
}
const AgileManagement = (props: AgileManagementProps) => {
  const { urlParams, setUrlParameter } = props;
  const [inputAsins, setInputAsins] = useState<string>('');
  const [inputFlag, setInputFlag] = useState(false);
  const [validAlert, setvalidAlert] = useState(false);
  const [recommendFlag, setRecommendFlag] = useState(false);
  const [recommendationInput, setRecommendationInput] = useState<AgileRecommendationInput[]>([]);
  const [zoneDetails, setZoneDetails] = useState<AgileMacroplanogram | undefined>(undefined);
  const [swapTime, setSwapTime] = useState<number>(0);
  const [newAsinMetaData, setNewAsinMetaData] = useState<AsinMetadataEntry[] | undefined | null>(undefined);
  const [currentTab, setCurrentTab] = useState('add');
  const programType = useAppSelector(selectProgram);
  const location = useLocation() as LocationParams;
  const [urlParameters] = useState<AgileURLParameterTypes>(location.state);
  const asinList = Array.from(new Set(inputAsins.split(/[,\s]+/)));
  const [totalNewFacings, setTotalNewFacings] = useState(asinList.length);
  const { enableZoneListInHeader, setEnableZoneListInHeader } = useContext(AgileContext);
  const [listOfMacroplanogram, setListOfMacroplanogram] = useState<AgileMacroplanogram[] | undefined>(undefined);
  const [toasts, setToasts] = useState<ToasterToasts[]>([]);
  const [includeDraft, setIncludeDraft] = useState(false);
  const openBulkOperationWarningToaster = () => {
    setToasts(toasts.concat(bulkOperationWarning()));
  };
  const onCloseToast = (id: string) => setToasts(toasts.filter((t) => t.id !== id));

  if (programType == null) {
    throw new Error('ProgramType is not initialized');
  }
  useEffect(() => {
    if (!InputAsinsValidationUtils(asinList)) {
      setInputFlag(false);
      setRecommendFlag(false);
    }
  }, [asinList]);

  const onClick = () => {
    if (InputAsinsValidationUtils(asinList)) {
      setInputFlag(true);
      setvalidAlert(false);
    } else {
      setInputFlag(false);
      setvalidAlert(true);
    }
  };

  const setZoneDetailsAndTime = (
    zoneInfo: AgileMacroplanogram | undefined,
    swapTimeStamp: number,
    macroplanogramList: AgileMacroplanogram[] | undefined,
  ) => {
    setZoneDetails(zoneInfo);
    setSwapTime(swapTimeStamp);
    setListOfMacroplanogram(macroplanogramList);
  };

  useEffect(() => {
    setEnableZoneListInHeader(currentTab !== 'bulkOperation');
    if (currentTab === 'bulkOperation') {
      openBulkOperationWarningToaster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, setEnableZoneListInHeader]);

  let number = 0;
  const rows: Record<number, FixtureComponent[]> = {};
  const zoneName = zoneDetails ? getConcatnatedSubcategoryName(zoneDetails) : ' ';
  zoneDetails?.fixtureIds?.forEach((fixtureId) => {
    // show at most 4 fixtures in one row
    if (rows[number] && rows[number].length === 4) {
      number += 1;
    }
    // initiate a new row to store FixtureBox component
    if (!rows[number]) {
      rows[number] = [];
    }

    const isEvenRow = number % 2 === 0;
    rows[number].push(
      <FixtureBox
        key={fixtureId}
        zoneId={zoneDetails.entryId}
        zoneName={zoneName}
        fixtureIdLabel={fixtureId}
        zoneColors={zoneColors}
        getZoneColor={getZoneColor}
        labelOnTopOfFixture={isEvenRow}
      />,
    );
  });

  return (
    <>
      {validAlert ? (
        <Alert size="large" type="error" onClose={() => setvalidAlert(false)}>
          Invalid Input! Asin should follow regex /[0-9]
          {9}
          [0-9X]|[A-Z][A-Z0-9]
          {9}/
        </Alert>
      ) : null}
      <Box type="outline" spacingInset="300">
        <Column alignmentHorizontal="start">
          <Heading level={2}>Agile Merchandising Dashboard</Heading>
          <Row alignmentVertical="bottom">
            <AtAgileHeader
              setZoneDetailsAndTime={setZoneDetailsAndTime}
              urlParams={urlParameters ?? urlParams}
              setUrlParameter={setUrlParameter}
              setRecommendationFlag={setRecommendFlag}
              enableZoneListInHeader={enableZoneListInHeader}
              includeDraft={includeDraft}
              setIncludeDraft={setIncludeDraft}
            />
          </Row>
        </Column>
      </Box>
      <Row spacing="400" alignmentHorizontal="start" spacingInset="400">
        <TabGroup value={currentTab} onChange={setCurrentTab} aria-labelledby="tablist-heading" type="classic">
          <Tab value="add" aria-controls="tabpanel-modify" id="add">
            Add
          </Tab>
          <Tab value="modify" aria-controls="tabpanel-modify" id="modify">
            Modify/Reduce
          </Tab>
          <Tab value="bulkOperation" aria-controls="tabpanel-modify" id="bulkOperation">
            Bulk Operation
          </Tab>
        </TabGroup>
      </Row>
      <Divider />
      {currentTab === 'add' && (
        <>
          {`Use this page to add products into the ${zoneName} zone.`}
          {includeDraft && (
            <Row alignmentHorizontal="center">
              <Alert size="large" type="informational">
                You are working in a draft zone - all assortment additions and modifications will be saved as drafts for
                later review
              </Alert>
            </Row>
          )}
          <Row widths={['35%', '40%']} spacingInset="200" alignmentHorizontal="start" backgroundColor="subdued">
            <Row>
              <div>
                {Object.keys(rows).length > 0 && (
                  <>
                    <h4>Zone Preview:</h4>
                    {Object.keys(rows).map((rowKey) => (
                      <div key={rowKey} className="row">
                        {rows[Number(rowKey)]}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </Row>
            <Row>
              <Textarea
                value={inputAsins}
                onChange={setInputAsins}
                placeholder="Enter a list of ASINs that you would like to add to this zone"
                error={getAsinValidationMessage(asinList)}
                resize="auto"
                rows={5}
                errorMessage={getAsinValidationMessage(asinList) ? 'Invalid List of Asins' : null}
                helperText="For multiple ASINs use commas (,) eg: B00XXXXXFW, B00XXXXXF6"
                width={500}
              />
            </Row>
          </Row>
          <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
            <Button onClick={onClick} disabled={zoneDetails === undefined || !InputAsinsValidationUtils(asinList)}>
              Next
            </Button>
          </Row>
          {inputFlag ? (
            <AtFaceUnitEditTable
              asinList={asinList}
              zoneDetails={zoneDetails}
              setRecommendationInput={setRecommendationInput}
              setRecommendationFlag={setRecommendFlag}
              setNewAsinMetaData={setNewAsinMetaData}
              setTotalNewFacings={setTotalNewFacings}
              totalNewFacings={totalNewFacings}
              setInputFlag={setInputFlag}
            />
          ) : null}
          {recommendFlag && (
            <AtAddRecommendationTable
              recommendationInput={recommendationInput}
              zoneDetails={zoneDetails}
              swapTime={swapTime}
              newAsinMetaData={newAsinMetaData}
              totalNewFacings={totalNewFacings}
              isDraft={includeDraft}
            />
          )}
        </>
      )}
      {currentTab === 'modify' && zoneDetails && swapTime && (
        <AtModifyRecommendationTable
          zoneDetails={zoneDetails}
          swapTime={swapTime}
          isDraft={includeDraft}
          allZones={listOfMacroplanogram || []}
        />
      )}
      {currentTab === 'bulkOperation' ? (
        <>
          Use this page to make bulk changes across zones.
          <Toaster toasts={toasts} onCloseToast={onCloseToast}>
            {(toast) => <Alert toast {...toast} type="warning" />}
          </Toaster>
          {includeDraft && (
            <Row alignmentHorizontal="center">
              <Alert size="large" type="informational">
                Draft Mode Active: All assortment changes (additions and modifications) made in this section will be
                saved as drafts until published
              </Alert>
            </Row>
          )}
          <AtAcrossZoneMain
            listOfMacroplanogram={listOfMacroplanogram}
            swapTime={swapTime}
            includeDraft={includeDraft}
          />
        </>
      ) : null}
    </>
  );
};
export default AgileManagement;
